import { Cable, ChevronRight, ExternalLink } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { WorkflowsSection } from '@/features/workflows'
import { cn } from '@/lib/utils'
import { CobaltLaunchButton } from './CobaltLaunchButton'
import type { CobaltConnectionStatus, CredentialStatusResponse } from './types'

interface ConnectionsViewProps {
  credentialStatus?: CredentialStatusResponse['status']
  candidateStatus?: CredentialStatusResponse['candidate_status']
  canSubmitCredentials: boolean
  canValidateCredentials: boolean
  isLoading: boolean
  isError: boolean
  isOpening: boolean
  onOpenIntake: () => void
  onOpenConnection: () => void
  onReview: () => void
  cobaltStatus?: CobaltConnectionStatus
  canStartCobaltOnboarding: boolean
  isRetrying: boolean
  onRetryCobaltStatus: () => void
  isCobaltStarting: boolean
  onStartCobaltOnboarding: () => void
}

export function ConnectionsView({
  credentialStatus,
  candidateStatus,
  canSubmitCredentials,
  canValidateCredentials,
  isLoading,
  isError,
  isOpening,
  onOpenIntake,
  onOpenConnection,
  onReview,
  cobaltStatus,
  canStartCobaltOnboarding,
  isRetrying,
  onRetryCobaltStatus,
  isCobaltStarting,
  onStartCobaltOnboarding,
}: ConnectionsViewProps) {
  const credentialConnected =
    credentialStatus === 'configured' && !isLoading && !isError
  const cobaltConnected = cobaltStatus === 'connected' && !isLoading && !isError
  const isConnected = credentialConnected || cobaltConnected
  const cobaltNeedsAttention =
    cobaltStatus === 'needs_reauthentication' ||
    cobaltStatus === 'invalid_token'
  const statusLabel =
    candidateStatus === 'submitted'
      ? 'Submitted'
      : credentialConnected || cobaltConnected
        ? 'Connected'
        : isLoading
          ? 'Checking…'
          : isError ||
              candidateStatus === 'unavailable' ||
              credentialStatus === 'unavailable'
            ? 'Unavailable'
            : 'Not configured'
  const statusTone =
    candidateStatus === 'submitted' || credentialConnected || cobaltConnected
      ? 'border-transparent bg-success-ui-bg text-success-solid'
      : isError ||
          candidateStatus === 'unavailable' ||
          credentialStatus === 'unavailable'
        ? 'border-transparent bg-warning-ui-bg text-warning-solid'
        : 'border-border bg-muted/30 text-muted-foreground'
  const canSubmit =
    canSubmitCredentials &&
    !isLoading &&
    !isError &&
    credentialStatus === 'not_configured' &&
    candidateStatus === 'none'
  const needsCobaltOnboarding =
    cobaltStatus === 'not_connected' || cobaltNeedsAttention
  const canConnectCobalt =
    canStartCobaltOnboarding && !isLoading && !isError && needsCobaltOnboarding

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-5 py-7 lg:px-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-primary-high-contrast">
            Connections
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage the systems this organization uses.
          </p>
        </header>

        {/* Whole-card click affordance via the stretched-link pattern: the
            title button's `after:absolute after:inset-0` overlay extends its
            hit area to the whole card, so the focus ring lives on the button
            that actually takes focus rather than on the Card. The action
            cluster (relative) stays above the overlay and independently
            clickable. */}
        <Card
          className={cn(
            'relative gap-4 px-5 py-5 sm:flex-row sm:items-center',
            isConnected && 'transition-colors hover:bg-muted/20'
          )}>
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <Cable
              size={22}
              strokeWidth={1.5}
              className="mt-0.5 shrink-0 text-primary"
            />
            <div className="min-w-0 flex-1 space-y-2">
              <h2 className="text-base font-medium text-primary-high-contrast">
                {isConnected ? (
                  <button
                    type="button"
                    onClick={onOpenConnection}
                    className="cursor-pointer text-left after:absolute after:inset-0 after:rounded-xl focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    eClinicalWorks
                  </button>
                ) : (
                  'eClinicalWorks'
                )}
              </h2>
              <p className="text-sm text-muted-foreground">
                Credentials and data synchronization for Careforce workflows.
              </p>
            </div>
          </div>
          <div className="relative flex shrink-0 items-center justify-between gap-3 sm:self-center">
            <Badge
              role="status"
              variant="outline"
              className={cn('pointer-events-none px-2.5 py-1', statusTone)}>
              {statusLabel}
            </Badge>
            {canSubmit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isOpening}
                onClick={onOpenIntake}>
                {isOpening ? 'Opening…' : 'Add credentials'}
                {!isOpening && <ExternalLink aria-hidden="true" />}
              </Button>
            )}
            {canValidateCredentials && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="review-connection"
                onClick={onReview}>
                Review
              </Button>
            )}
            {canConnectCobalt && (
              <CobaltLaunchButton
                size="sm"
                isStarting={isCobaltStarting}
                onClick={onStartCobaltOnboarding}>
                {cobaltNeedsAttention ? 'Reconnect' : 'Connect'}
              </CobaltLaunchButton>
            )}
            {(cobaltStatus === 'unavailable' || isError) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isRetrying}
                onClick={onRetryCobaltStatus}>
                {isRetrying ? 'Checking…' : 'Retry status'}
              </Button>
            )}
            {isConnected && (
              <ChevronRight
                aria-hidden="true"
                className="pointer-events-none size-4 text-muted-foreground"
              />
            )}
          </div>
        </Card>

        {/* Workflows run against the connections above, so they read as the
            second half of this page rather than a page of their own. The
            section fetches, gates and bounds itself — see `WorkflowsSection`.
            The two halves of this page have nothing to do with each other, so
            a bad workflow row must not take the credential status down with
            it; the boundary that guarantees that lives with the section. */}
        <WorkflowsSection />
      </div>
    </div>
  )
}
